import { Deferred, Link, router, usePage } from '@inertiajs/react'
import { DataTable } from "@/components/ui/data-table"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/status-badges"
import { Search } from "lucide-react"
import { ColumnDef, Row } from "@tanstack/react-table"
import { useState } from "react"
import { format } from 'date-fns'
import { SortableHeader } from '@/components/sortable-header'
import { DealProgress } from '@/components/deal-progress'

export interface Deal {
  id: number
  name: string
  salesforce_id: string
  sku_id: number
  buyer: {
    id: number
    name: string
  }
  team: { id: number; name: string }
  requests?: string[]
  agent?: {
    name: string
  }
  state: string
  expiry_policy: string
  event_date: string
  start_date: string
  end_date: string
  months_total: number
  months_remaining: number
  fee_rate: number
  reporting_basis: string
  initial_term: number
  renewals: number
  renewal_term: number
  renewals_taken: number
  fee_unit: string
  fee_cap: number
  archived_at: string | null
  notes: string
  terms: Array<{
    name: string
    length: number
    start_date: string
    end_date: string
    sku: string
    taken?: boolean
    taken_status?: string
  }>
}

interface Props {
  showActions?: boolean
  title?: string
  subtitle?: string
  hiddenColumns?: string[]
}

interface PageProps {
  deals: Deal[]
  [key: string]: any
}

const columns: ColumnDef<Deal>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column} title="Name" />,
    enableSorting: true,
    cell: ({ row }) => (
      <div className="max-w-[180px] truncate">
        <Link href={`/deals/${row.original.id}`} className="hover:underline">
          {row.getValue("name")}
        </Link>
      </div>
    ),
  },
  {
    accessorKey: "buyer.name",
    header: ({ column }) => <SortableHeader column={column} title="Buyer" />,
    enableSorting: true,
    cell: ({ row }) => (
      <div className="max-w-[180px] truncate">
        <Link href={`/buyers/${row.original.buyer.id}`} className="hover:underline">
          {row.getValue("buyer_name")}
        </Link>
      </div>
    ),
  },
  {
    accessorKey: "team.name",
    header: ({ column }) => <SortableHeader column={column} title="Team" />,
    enableSorting: true,
    cell: ({ row }) => (
      <div className="max-w-[180px] truncate">
        <Link href={`/teams/${row.original.team.id}`} className="hover:underline">
          {row.getValue("team_name")}
        </Link>
      </div>
    ),
  },
  {
    accessorKey: "requests",
    header: ({ column }) => <SortableHeader column={column} title="Requests" />,
    enableSorting: true,
    cell: ({ row }) => {
      const requestTypes = row.original.requests || []
      return (
        <div className="flex gap-1">
          {requestTypes.map((requestType: string) => (
            <StatusBadge key={requestType} status={{ type: 'updateRequest', status: requestType } as any} className="rounded-xl opacity-60" />
          ))}
        </div>
      )
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => <SortableHeader column={column} title="Status" />,
    enableSorting: true,
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      return (
        <StatusBadge status={{ type: 'deal', status: status } as any} className="rounded-xl opacity-60" />
      )
    },
  },
  {
    accessorKey: "expiry_policy",
    header: ({ column }) => <SortableHeader column={column} title="Policy" />,
    enableSorting: true,
    cell: ({ row }) => {
      const policy = row.getValue("expiry_policy") as string;
      return <StatusBadge status={{ type: 'expiryPolicy', status: policy } as any} className="rounded-xl opacity-60" />
    },
  },
  {
    accessorKey: "end_date",
    header: ({ column }) => <SortableHeader column={column} title="End Date" />,
    cell: ({ row }) => {
      const endDate = row.getValue("end_date") as string
      return endDate ? format(new Date(endDate), "MMM d, yyyy") : null
    },
  },
  {
    accessorKey: "months_total",
    header: "Remaining",
    cell: ({ row }) => {
      const deal = row.original
      return <DealProgress
        numerator={deal.months_remaining}
        denominator={deal.months_total}
        label="months"
        variant="remaining"
        className="opacity-70"
      />
    },
  },
  {
    accessorKey: "renewals",
    header: "Renewals",
    cell: ({ row }) => {
      const deal = row.original
      return <DealProgress
        numerator={deal.renewals_taken}
        denominator={deal.renewals}
        label="taken"
        className="opacity-70"
      />
    },
  },
  {
    accessorKey: "sku",
    header: ({ column }) => <SortableHeader column={column} title="SKU" />,
    enableSorting: true,
  },
  {
    accessorKey: "fee_rate",
    header: ({ column }) => <SortableHeader column={column} title="Fee Rate" />,
    enableSorting: true,
    cell: ({ row }) => {
      const feeRate = row.getValue("fee_rate") as number
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(feeRate)
      return formatted
    },
  },
  {
    accessorKey: "fee_unit",
    header: "Fee Unit",
    cell: ({ row }) => {
      const feeUnit = row.getValue("fee_unit") as string
      return feeUnit.charAt(0).toUpperCase() + feeUnit.slice(1)
    }
  }
]

export function DealsTable({ showActions = true, title = "", subtitle = "", hiddenColumns = [] }: Props) {
  const { deals } = usePage<PageProps>().props
  const [globalFilter, setGlobalFilter] = useState("")

  // Filter out hidden columns
  const visibleColumns = columns.filter(column => {
    if ('accessorKey' in column && column.accessorKey) {
      return !hiddenColumns.includes(column.accessorKey as string);
    }
    return true;
  })

  const tableColumns = showActions ? [
    ...visibleColumns,
    {
      id: "actions",
      cell: ({ row }: { row: Row<Deal> }) => {
        const deal = row.original;
        return (
          <div className="flex items-center gap-2">
            <Link href={`/deals/${deal.id}`}>
              <Button variant="outline" size="sm">
                View
              </Button>
            </Link>
            <Link href={`/deals/${deal.id}/edit`}>
              <Button variant="outline" size="sm">
                Edit
              </Button>
            </Link>
          </div>
        );
      },
    },
  ] : visibleColumns

  return (
    <>
      <div className="space-y-4 pt-4 px-6">
        <div className="flex items-center justify-between place-items-bo">
          <div>
            {subtitle ? (
              <h1 className="text-3xl font-bold">{title}</h1>
            ) : (
              <h2 className="text-2xl font-bold">{title}</h2>
            )}
            {subtitle && (
              <div className="text-sm text-muted-foreground">
                {subtitle}
              </div>
            )}
          </div>
          <div className="relative w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter deals..."
              value={globalFilter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGlobalFilter(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="w-full overflow-auto">
          <Deferred data="deals" fallback={<div>Loading...</div>}>
            <DataTable
              columns={tableColumns}
              data={deals}
              globalFilter={globalFilter}
              onGlobalFilterChange={setGlobalFilter}
            />
          </Deferred>
        </div>
      </div>
    </>
  )
}
